import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, XCircle, CircleNotch, Warning, ArrowRight } from "@phosphor-icons/react";
import Logomark from "@/components/Logomark";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const MAX_POLLS = 12;
const POLL_MS = 2500;

export default function PaymentResult() {
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const { checkAuth } = useAuth();
  const cancelled = location.pathname.includes("cancel");
  const sessionId = params.get("session_id");
  const [state, setState] = useState(cancelled ? "cancelled" : "checking");
  const polls = useRef(0);

  useEffect(() => {
    if (cancelled || !sessionId) {
      if (!cancelled && !sessionId) setState("error");
      return;
    }
    let stop = false;
    const poll = async () => {
      if (stop) return;
      polls.current += 1;
      try {
        const res = await api.paymentStatus(sessionId);
        if (res.payment_status === "paid") {
          setState("paid");
          checkAuth(); // refresh pass status in the auth context
          return;
        }
        if (res.status === "expired" || res.payment_status === "failed") {
          setState("error");
          return;
        }
      } catch {
        // transient — keep polling
      }
      if (polls.current >= MAX_POLLS) { setState("timeout"); return; }
      setTimeout(poll, POLL_MS);
    };
    poll();
    return () => { stop = true; };
  }, [cancelled, sessionId, checkAuth]);

  const body = {
    checking: {
      icon: <CircleNotch size={44} className="animate-spin text-gray-400" />,
      title: "Confirming your payment…",
      text: "Hold tight — we're confirming your purchase with Stripe. This usually takes a few seconds.",
      testid: "payment-checking",
    },
    paid: {
      icon: <CheckCircle size={48} weight="fill" className="text-emerald-600" />,
      title: "Your Xartis Pass is active",
      text: "Payment confirmed. Full access is unlocked — plan builds, Atlas AI refinement and PDF/DOCX exports are all live on your account.",
      testid: "payment-success",
      cta: { label: "Start planning", to: "/start" },
    },
    cancelled: {
      icon: <XCircle size={48} weight="fill" className="text-gray-400" />,
      title: "Checkout cancelled",
      text: "No payment was taken. Your account is unchanged — you can pick a pass whenever you're ready.",
      testid: "payment-cancelled",
      cta: { label: "Back to passes", to: "/pricing" },
    },
    timeout: {
      icon: <Warning size={48} weight="fill" className="text-amber-500" />,
      title: "Still confirming…",
      text: "Your payment is taking longer than usual to confirm. If you completed checkout, your pass will activate automatically within a few minutes — check your library shortly.",
      testid: "payment-timeout",
      cta: { label: "Go to My Plans", to: "/library" },
    },
    error: {
      icon: <XCircle size={48} weight="fill" className="text-red-500" />,
      title: "Payment not completed",
      text: "We couldn't confirm this payment. No pass was activated. If you believe you were charged, contact support — otherwise try again.",
      testid: "payment-error",
      cta: { label: "Back to passes", to: "/pricing" },
    },
  }[state];

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <header className="border-b border-gray-200 px-6 sm:px-10 py-4 flex items-center gap-2">
        <button onClick={() => navigate("/")} className="flex items-center gap-2" data-testid="payment-home-link">
          <Logomark size={24} />
          <span className="font-display font-black text-2xl tracking-tight">Xartis</span>
        </button>
      </header>
      <main className="flex-1 flex items-center justify-center px-8 py-16">
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
          className="max-w-md text-center space-y-5" data-testid={body.testid}>
          <div className="flex justify-center">{body.icon}</div>
          <h1 className="font-display font-black text-3xl tracking-tight">{body.title}</h1>
          <p className="text-base text-gray-600 leading-relaxed">{body.text}</p>
          {body.cta && (
            <button onClick={() => navigate(body.cta.to)} data-testid="payment-result-cta"
              className="inline-flex items-center gap-2 bg-gray-950 hover:bg-gray-700 text-white px-6 py-3 text-sm font-bold transition-colors">
              {body.cta.label} <ArrowRight size={15} weight="bold" />
            </button>
          )}
        </motion.div>
      </main>
    </div>
  );
}
